import { Screens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { ExplorerActionRow } from 'screens/AssessmentScreen/Explorer/ExplorerActionRow';
import { ExplorerControlRow } from 'screens/AssessmentScreen/Explorer/ExplorerControlRow';
import { ExplorerQuestionRow } from 'screens/AssessmentScreen/Explorer/ExplorerQuestionRow';
import {
  selectAseChecklistItemForQuestion,
  selectAseTplChecklistItemsForQuestion,
  selectAseQuestionActionIds,
  selectAseQuestionControlIds,
  selectAseTplQuestion,
  selectAseTplQuestionIsVisible,
  selectAseQuestionUnreviewedActionIds,
  selectAseQuestionUnreviewedControlIds,
  selectAseResultQuestion,
  selectAseTplAnswer,
  selectAseExplorerOptionQuestionsNegative,
  selectAseExplorerOptionQuestionsPositive,
  selectAseExplorerOptionQuestionsUnanswered,
  selectAseIsEditable,
  selectAseExplorerOptionCollapseFinding,
  selectAseExplorerOptionQuestionDescriptions,
} from 'store/assessments/selectors';
import { AssessmentQuestionType } from 'types/types';
import { getChecklistOutcome } from 'lib/riskbase';
import {
  ChecklistItemResultEnum,
  InstanceParentParams,
} from 'store/assessments/types';
import { theme } from 'lib/theme';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doAseOpenQuestion } from 'store/assessments/actions';
import { useNavigationIsSelectedMulti } from 'hooks/useNavigationIsSelected';

export const ExplorerQuestion: React.FC<{
  id: string;
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({ id, fieldInstanceId, parent }) => {
  const dispatch = useDispatch();

  const optionQuestionDescriptions = useSelector(
    selectAseExplorerOptionQuestionDescriptions,
  );
  const collapseFindings = useSelector(selectAseExplorerOptionCollapseFinding);
  const isEditable = useSelector(selectAseIsEditable);
  const showQuestionsPositive = useSelector(
    selectAseExplorerOptionQuestionsPositive,
  );
  const showQuestionsNegative = useSelector(
    selectAseExplorerOptionQuestionsNegative,
  );
  const showQuestionsUnanswered = useSelector(
    selectAseExplorerOptionQuestionsUnanswered,
  );

  const questionTemplate = useSelector((state) =>
    selectAseTplQuestion(state, id),
  );
  const templateQuestionIsVisible = useSelector((state) =>
    selectAseTplQuestionIsVisible(state, id, fieldInstanceId),
  );
  const questionResult = useSelector(
    (state) => selectAseResultQuestion(state, id, fieldInstanceId),
    isEqual,
  );
  const answer = useSelector((state) =>
    selectAseTplAnswer(state, questionResult?.answerId || '-1'),
  );

  const checklistItems = useSelector(
    (state) =>
      selectAseTplChecklistItemsForQuestion(state, id, fieldInstanceId),
    isEqual,
  );
  const checklistItemResults = useSelector(
    (state) => selectAseChecklistItemForQuestion(state, id, fieldInstanceId),
    isEqual,
  );
  const actionIds = useSelector(
    (state) => selectAseQuestionActionIds(state, id, fieldInstanceId),
    isEqual,
  );
  const unreviewedActionIds = useSelector(
    (state) => selectAseQuestionUnreviewedActionIds(state, id, fieldInstanceId),
    isEqual,
  );
  const controlIds = useSelector(
    (state) => selectAseQuestionControlIds(state, id, fieldInstanceId),
    isEqual,
  );
  const unreviewedControlIds = useSelector(
    (state) =>
      selectAseQuestionUnreviewedControlIds(state, id, fieldInstanceId),
    isEqual,
  );

  const selected = useNavigationIsSelectedMulti(
    collapseFindings
      ? [
          fieldInstanceId
            ? {
                name: Screens.ASEREPEATABLE_QUESTION,
                params: { id, fieldInstanceId, parent },
              }
            : {
                name: Screens.ASE_QUESTION,
                params: { id: questionTemplate.id, fieldInstanceId, parent },
              },
          ...[...unreviewedActionIds, ...actionIds].map((actionId) =>
            fieldInstanceId
              ? {
                  name: Screens.ASEREPEATABLE_ACTION,
                  params: { id: actionId, parent },
                }
              : { name: Screens.ASE_ACTION, params: { id: actionId, parent } },
          ),
          ...[...unreviewedControlIds, ...controlIds].map((controlId) =>
            fieldInstanceId
              ? {
                  name: Screens.ASEREPEATABLE_CONTROL,
                  params: { id: controlId, parent },
                }
              : {
                  name: Screens.ASE_CONTROL,
                  params: { id: controlId, parent },
                },
          ),
        ]
      : [],
  );

  if (!templateQuestionIsVisible) {
    return null;
  }

  if (questionTemplate.type === AssessmentQuestionType.checklist) {
    const checklistResult = getChecklistOutcome(
      isEditable,
      checklistItems,
      checklistItemResults,
    );
    if (!checklistResult) {
      if (!showQuestionsUnanswered) {
        return null;
      }
    } else if (
      !(showQuestionsUnanswered && checklistResult.percentage < 100) &&
      (checklistResult.result === ChecklistItemResultEnum.cross
        ? !showQuestionsNegative
        : !showQuestionsPositive)
    ) {
      return null;
    }
  } else if (!answer) {
    if (!showQuestionsUnanswered) {
      return null;
    }
  } else if (answer.negative) {
    if (!showQuestionsNegative) {
      return null;
    }
  } else if (!showQuestionsPositive) {
    return null;
  }

  return (
    <View
      style={{
        borderLeftWidth:
          questionTemplate.level > 1
            ? ((questionTemplate.level - 1) * theme.paddingHorizontal) / 2
            : 0,
        borderLeftColor: '#ccc',
      }}
    >
      <SeparatorHorizontal />
      <CardRow
        navigationIsSelected={
          fieldInstanceId
            ? {
                name: Screens.ASEREPEATABLE_QUESTION,
                params: { id, fieldInstanceId, parent },
              }
            : {
                name: Screens.ASE_QUESTION,
                params: { id: questionTemplate.id, fieldInstanceId, parent },
              }
        }
        onPress={() =>
          dispatch(doAseOpenQuestion({ id, fieldInstanceId, parent }))
        }
        style={{ paddingLeft: 0, paddingRight: 0, minHeight: 0 }}
        contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
      >
        <ExplorerQuestionRow
          key={questionTemplate.id}
          question={questionTemplate}
          answer={answer}
          checklistItemResults={checklistItemResults}
          checklistItems={checklistItems}
          optionQuestionDescriptions={optionQuestionDescriptions}
          actionsCount={!selected && collapseFindings ? actionIds.length : 0}
          controlsCount={!selected && collapseFindings ? controlIds.length : 0}
          unreviewedActionsCount={
            !selected && collapseFindings ? unreviewedActionIds.length : 0
          }
          unreviewedControlsCount={
            !selected && collapseFindings ? unreviewedControlIds.length : 0
          }
        />
        {(selected || !collapseFindings) &&
          (!!actionIds.length ||
            !!controlIds.length ||
            !!unreviewedActionIds.length ||
            !!unreviewedControlIds.length) && (
            <Card
              expanded
              margin={false}
              style={{
                marginBottom: theme.paddingHorizontal,
              }}
              bodyStyle={{
                borderColor: '#f1f1f1',
              }}
            >
              {actionIds.map((actionId) => (
                <ExplorerActionRow
                  key={actionId}
                  id={actionId}
                  parent={parent}
                />
              ))}
              {controlIds.map((controlId) => (
                <ExplorerControlRow
                  key={controlId}
                  id={controlId}
                  parent={parent}
                />
              ))}
              {unreviewedActionIds.map((actionId) => (
                <ExplorerActionRow
                  key={`review-${actionId}`}
                  id={actionId}
                  parent={parent}
                />
              ))}
              {unreviewedControlIds.map((controlId) => (
                <ExplorerControlRow
                  key={`review-${controlId}`}
                  id={controlId}
                  parent={parent}
                />
              ))}
            </Card>
          )}
      </CardRow>
    </View>
  );
};
